import React, { Component } from "react";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import { Alert, Modal, Table, Tag, Checkbox } from "antd";
import ImportadorExcelGenerico from "../../../../Components/ImportadorExcelGenerico";
import { TIPOS_SERVICIO } from "../cmmsComun";
import "../cmms.css";

// Importar el programa de un contrato desde Excel (como el Planeador / las hojas "1ra Vuelta"
// de los controles por cliente). Cada renglon = un equipo en una vuelta, con fecha,
// ingeniero y folio externo opcionales. Si el equipo no estaba en el contrato, entra.
// Se abre con history.push("/panel/cmmsImportarProgramaContrato", { contratoId, volverA }).

const normalizar = (t) =>
    String(t ?? "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

// Dia que existe de verdad (31/02 no; Date.UTC lo correria al 3 de marzo).
const diaValido = (anio, mes, dia) => {
    const d = new Date(Date.UTC(anio, mes - 1, dia));
    return d.getUTCFullYear() === anio && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia;
};
const componer = (anio, mes, dia) =>
    diaValido(Number(anio), Number(mes), Number(dia)) ? { ok: true, valor: `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}` } : { ok: false };

// Fecha del Excel: numero serial, dd/mm/aaaa o aaaa-mm-dd -> "aaaa-mm-dd" (o null).
const fechaExcel = (v) => {
    if (v === undefined || v === null || v === "") return { ok: true, valor: null };
    if (typeof v === "number") {
        const d = new Date(Math.round((v - 25569) * 86400 * 1000));
        return isNaN(d.getTime()) ? { ok: false } : { ok: true, valor: d.toISOString().slice(0, 10) };
    }
    const t = String(v).trim();
    let m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) return componer(m[3], m[2], m[1]);
    m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return componer(m[1], m[2], m[3]);
    return { ok: false };
};

const COLUMNAS = ["Equipo", "Vuelta", "Fecha", "Ingeniero", "Folio externo", "Tipo de servicio", "Ref: Tipo de activo", "Ref: Modelo", "Ref: Serie", "Ref: Inventario"];

const agregarAIndice = (mapa, llave, valor) => {
    const k = normalizar(llave);
    if (!k) return;
    if (!mapa.has(k)) mapa.set(k, []);
    if (!mapa.get(k).includes(valor)) mapa.get(k).push(valor);
};

const prepararCatalogos = (data) => {
    const porCodigo = new Map();
    const porReferencia = new Map();
    (data.activos || []).forEach((a) => {
        if (a.codigo) porCodigo.set(normalizar(a.codigo), a);
        [a.idExterno, a.numeroSerie, a.numeroInventario, a.idInterno].forEach((x) => agregarAIndice(porReferencia, x, a));
    });
    const vueltas = data.contrato?.vueltas || [];
    const vueltaPorTexto = new Map();
    vueltas.forEach((v, i) => {
        vueltaPorTexto.set(normalizar(v.nombre), v);
        vueltaPorTexto.set(String(i + 1), v);
    });
    const empleados = new Map();
    (data.empleados || []).forEach((e) => agregarAIndice(empleados, e.nombre, e));
    const tipos = new Map((data.tiposServicio || TIPOS_SERVICIO).map((t) => [normalizar(t.label), t]));
    tipos.set("preventivo", { value: 1, label: "Mantenimiento Preventivo" });
    tipos.set("correctivo", { value: 2, label: "Mantenimiento Correctivo" });
    return { ...data, porCodigo, porReferencia, vueltas, vueltaPorTexto, empleados, tipos };
};

// La plantilla ya trae cada equipo del cliente en cada vuelta: se borran los renglones que no van.
const construirPlantilla = (cat) => {
    const wb = XLSX.utils.book_new();
    const filas = [];
    (cat?.activos || []).slice(0, 2500).forEach((a) =>
        (cat?.vueltas || []).forEach((v) =>
            filas.push({
                Equipo: a.codigo,
                Vuelta: v.nombre,
                Fecha: null,
                Ingeniero: null,
                "Folio externo": null,
                "Tipo de servicio": null,
                "Ref: Tipo de activo": a.tipoNombre || null,
                "Ref: Modelo": a.modelo || null,
                "Ref: Serie": a.numeroSerie || null,
                "Ref: Inventario": a.numeroInventario || null,
            })
        )
    );
    if (!filas.length) filas.push({ Equipo: "AST00001", Vuelta: "1ra Vuelta", Fecha: "15/03/2026", Ingeniero: "", "Folio externo": "SER10000118022", "Tipo de servicio": "Mantenimiento Preventivo" });
    const hoja = XLSX.utils.json_to_sheet(filas, { header: COLUMNAS });
    hoja["!cols"] = [{ wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 28 }, { wch: 18 }, { wch: 24 }, { wch: 24 }, { wch: 16 }, { wch: 18 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, hoja, "Programa");
    XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet((cat?.vueltas || []).map((v, i) => ({ Numero: i + 1, Vuelta: v.nombre, Del: String(v.fechaInicio).slice(0, 10), Al: String(v.fechaFin).slice(0, 10) }))),
        "Vueltas"
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((cat?.empleados || []).map((e) => ({ Ingeniero: e.nombre }))), "Ingenieros");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(TIPOS_SERVICIO.map((t) => ({ "Tipo de servicio": t.label }))), "Tipos de servicio");
    return wb;
};

const validarFila = (fila, cat, _indice, contexto) => {
    const errores = [];
    const get = (col) => (fila[col] === undefined || fila[col] === null ? "" : String(fila[col]).trim());

    // Equipo: folio AST..., o ID externo / serie / inventario (si es unico en el cliente).
    const textoEquipo = get("Equipo");
    let activo = null;
    if (!textoEquipo) errores.push({ campo: "Equipo", motivo: "está vacío" });
    else {
        activo = cat.porCodigo.get(normalizar(textoEquipo)) || null;
        if (!activo) {
            const candidatos = cat.porReferencia.get(normalizar(textoEquipo)) || [];
            if (candidatos.length === 1) activo = candidatos[0];
            else if (candidatos.length > 1) errores.push({ campo: "Equipo", motivo: `"${textoEquipo}" coincide con ${candidatos.length} equipos: use el folio (AST…)` });
            else errores.push({ campo: "Equipo", motivo: `"${textoEquipo}" no es un equipo del cliente del contrato` });
        }
    }

    const textoVuelta = get("Vuelta");
    const vuelta = textoVuelta ? cat.vueltaPorTexto.get(normalizar(textoVuelta)) : null;
    if (!textoVuelta) errores.push({ campo: "Vuelta", motivo: "está vacía (nombre o número de la vuelta)" });
    else if (!vuelta) errores.push({ campo: "Vuelta", motivo: `"${textoVuelta}" no es una vuelta del contrato` });

    const f = fechaExcel(fila["Fecha"]);
    if (!f.ok) errores.push({ campo: "Fecha", motivo: "no es una fecha válida (use dd/mm/aaaa)" });
    else if (f.valor && cat.contrato && (f.valor < String(cat.contrato.fechaInicio).slice(0, 10) || f.valor > String(cat.contrato.fechaConclusion).slice(0, 10)))
        errores.push({ campo: "Fecha", motivo: "está fuera de la vigencia del contrato" });

    let empleado = null;
    const textoIng = get("Ingeniero");
    if (textoIng) {
        const exactos = cat.empleados.get(normalizar(textoIng)) || [];
        if (exactos.length === 1) empleado = exactos[0];
        else {
            const n = normalizar(textoIng);
            const parecidos = (cat.empleados ? [...cat.empleados.values()].flat() : []).filter((e) => normalizar(e.nombre).startsWith(n));
            if (parecidos.length === 1) empleado = parecidos[0];
            else errores.push({ campo: "Ingeniero", motivo: parecidos.length > 1 ? `"${textoIng}" coincide con ${parecidos.length} empleados: escriba el nombre completo` : `"${textoIng}" no es un empleado` });
        }
    }

    let tipo = { value: 1, label: "Mantenimiento Preventivo" };
    const textoTipo = get("Tipo de servicio");
    if (textoTipo) {
        tipo = cat.tipos.get(normalizar(textoTipo));
        if (!tipo) errores.push({ campo: "Tipo de servicio", motivo: `"${textoTipo}" no es un tipo de servicio` });
    }

    if (activo && vuelta && tipo) {
        const llave = `${activo._id}|${vuelta._id}|${tipo.value}`;
        if (contexto.clavesYaUsadas.has(llave)) errores.push({ campo: "Equipo", motivo: `repetido en esa vuelta (también en la fila ${contexto.clavesYaUsadas.get(llave)})` });
        else contexto.clavesYaUsadas.set(llave, (fila["__rowNum__"] ?? 0) + 1);
    }

    if (errores.length) return { ok: false, errores };
    return {
        ok: true,
        datos: {
            numeroFila: (fila["__rowNum__"] ?? 0) + 1,
            activoId: activo._id,
            vueltaId: vuelta._id,
            tipoServicio: tipo.value,
            fechaPlaneada: f.valor,
            empleadoId: empleado?._id || null,
            folioExterno: get("Folio externo"),
            _equipo: [activo.codigo, activo.tipoNombre, activo.numeroSerie].filter(Boolean).join(" · "),
            _vuelta: vuelta.nombre,
            _ingeniero: empleado?.nombre || "",
            _tipo: tipo.label,
            _enOtro: !!activo.enOtroContrato,
        },
    };
};

const columnasPreview = [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Equipo", dataIndex: "_equipo", key: "_equipo", width: 260, render: (v, r) => (<span>{v} {r._enOtro ? <Tag color="gold">En otro contrato</Tag> : null}</span>) },
    { title: "Vuelta", dataIndex: "_vuelta", key: "_vuelta", width: 110 },
    { title: "Fecha", dataIndex: "fechaPlaneada", key: "fechaPlaneada", width: 110, render: (v) => (v ? v.split("-").reverse().join("/") : "") },
    { title: "Ingeniero", dataIndex: "_ingeniero", key: "_ingeniero", width: 180 },
    { title: "Folio externo", dataIndex: "folioExterno", key: "folioExterno", width: 150 },
    { title: "Tipo", dataIndex: "_tipo", key: "_tipo", width: 170 },
];

class ImportarProgramaContrato extends Component {
    state = { mover: false, resultado: null };

    get contratoId() {
        return this.props.location?.state?.contratoId || null;
    }

    volver = () => {
        const destino = this.props.location?.state?.volverA;
        if (destino) this.props.history.replace(destino);
        else this.props.history.goBack();
    };

    render() {
        if (!this.contratoId) return <Alert type="warning" showIcon message="Abra el importador desde el contrato (paso Equipos › Importar programa)." />;
        const resultados = this.state.resultado?.resultados || [];
        const errores = resultados.filter((r) => r.estado === "error");
        return (
            <>
                <ImportadorExcelGenerico
                    {...this.props}
                    titulo="Importar programa del contrato"
                    nombreEntidadPlural="renglones del programa"
                    endpointDatosImportar={`cmms/datosImportarProgramaCMMS?contratoId=${this.contratoId}`}
                    endpointImportar="cmms/importarProgramaCMMS"
                    nombrePlantilla="plantillaProgramaContratoCMMS.xlsx"
                    prepararCatalogos={prepararCatalogos}
                    construirPlantilla={construirPlantilla}
                    validarFila={validarFila}
                    columnasPreview={columnasPreview}
                    tamanoLote={500}
                    prepararPayloadExtra={() => ({ contratoId: this.contratoId, mover: this.state.mover })}
                    instrucciones={[
                        "Descargue la plantilla: ya trae cada equipo del cliente en cada vuelta del contrato. Borre los renglones que no van (equipos que no son del contrato, o vueltas que no les tocan).",
                        "Equipo: el folio (AST…) o, si es único, su ID externo, serie o inventario. Vuelta: su nombre o número (1, 2…).",
                        "Fecha (dd/mm/aaaa), Ingeniero, Folio externo (ej. SER10000118022) y Tipo de servicio son opcionales; sin tipo se toma Mantenimiento Preventivo.",
                        "Si un equipo no estaba en el contrato, entra. Si el renglón ya existía y sigue pendiente, se le completan fecha, ingeniero y folio. Lo ya realizado no se toca.",
                        "Las columnas 'Ref:' son sólo de referencia y no se leen.",
                    ]}
                    controlesExtra={
                        <Checkbox style={{ margin: "10px 0" }} checked={this.state.mover} onChange={(e) => this.setState({ mover: e.target.checked })}>
                            Mover equipos que están en otro contrato (de su contrato anterior se borra lo pendiente que no tiene Orden de Servicio; lo realizado se conserva)
                        </Checkbox>
                    }
                    onImportSuccess={(resData) => {
                        this.setState({ resultado: resData });
                        return true;
                    }}
                />
                <Modal
                    className="cmms-modal"
                    visible={!!this.state.resultado}
                    title={<div className="cmms-modal-titulo-texto">Resultado de la importación</div>}
                    width={window.innerWidth > 900 ? 820 : "96%"}
                    onCancel={() => this.setState({ resultado: null })}
                    onOk={this.volver}
                    okText="Volver al contrato"
                    cancelText="Seguir importando"
                >
                    <div style={{ padding: 16 }}>
                        <Alert
                            type={errores.length ? "warning" : "success"}
                            showIcon
                            message={`${this.state.resultado?.numeroCreados || 0} renglón(es) nuevos · ${this.state.resultado?.numeroEditados || 0} completados${errores.length ? ` · ${errores.length} no se importaron` : ""}`}
                            style={{ marginBottom: 12 }}
                        />
                        {errores.length > 0 && (
                            <Table
                                size="small"
                                rowKey={(r, i) => `${r.numeroFila}-${i}`}
                                pagination={{ pageSize: 10 }}
                                dataSource={errores}
                                columns={[
                                    { title: "Fila", dataIndex: "numeroFila", width: 60 },
                                    { title: "Motivo", dataIndex: "motivo", render: (v) => <Tag color="orange" style={{ whiteSpace: "normal" }}>{v}</Tag> },
                                ]}
                            />
                        )}
                    </div>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportarProgramaContrato);
